
import React, { useState } from 'react';
import { Badge } from "@/components/ui/badge";
import { ChevronRight, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { EditableCell } from './renderers/EditableCell';
import { MultiSelectChips } from './MultiSelectChips';
import { MultiSelectPIC } from './MultiSelectPIC';
import { MultiSelectContentTypes } from './MultiSelectContentTypes';
import { CreatorInfo } from './CreatorInfo';
import { SearchableSelect } from "@/components/ui/searchable-select";
import { REGION_OPTIONS, TIER_OPTIONS, SOCIAL_MEDIA_OPTIONS, INTERNAL_KOL_TYPE_OPTIONS } from './constants/kolDictionaryConstants';

interface HierarchicalKolData {
  id: string;
  creator_name: string;
  social_media_type?: string;
  social_media_handler?: string;
  social_media_name?: string;
  payment_structure?: string;
  currency_type?: string;
  region?: string;
  reward_station_url?: string;
  reward_station_type?: string;
  metamorphosis_referral_code?: string;
  kol_shortcut?: string;
  kol_type?: string;
  internal_kol_type?: string;
  tier?: string;
  start_date?: string;
  end_date?: string;
  pic_id?: string[];
  content_type?: string[];
  price_content_type?: Record<string, number>;
  profile_image_url?: string;
  created_at?: string;
  updated_at?: string;
  parent_id?: string;
  children?: HierarchicalKolData[];
  isExpanded?: boolean;
}

interface KolHierarchicalDictionaryRowProps {
  data: HierarchicalKolData;
  onUpdate: (newRow: any, oldRow: any) => Promise<any>;
  isPending: (id: string, field?: string) => boolean;
  isOptimisticProcessing: boolean;
  contentTypeOptions: { value: string; label: string }[];
  picOptions: { value: string; label: string }[];
  handleUpdateContentTypes: (rowId: string, newValues: string[]) => Promise<void>;
  handleUpdatePIC: (rowId: string, newValues: string[]) => Promise<void>;
  handleUpdateKolType: (rowId: string, newValue: string) => Promise<void>;
  handleUpdateInternalKolType: (rowId: string, newValue: string) => Promise<void>;
  handleUpdateRegion: (rowId: string, newValue: string) => Promise<void>;
  handleUpdateTier: (rowId: string, newValue: string) => Promise<void>;
  applyOptimisticUpdate: (rowId: string, field: string, value: any) => void;
  level?: number;
}

export function KolHierarchicalDictionaryRow({ 
  data, 
  onUpdate,
  isPending, 
  isOptimisticProcessing,
  contentTypeOptions,
  picOptions,
  handleUpdateContentTypes,
  handleUpdatePIC,
  handleUpdateKolType,
  handleUpdateInternalKolType,
  handleUpdateRegion,
  handleUpdateTier,
  applyOptimisticUpdate,
  level = 0
}: KolHierarchicalDictionaryRowProps) {
  const [isExpanded, setIsExpanded] = useState(data.isExpanded || false);
  const { hasBuAccess } = useAuth();

  const hasChildren = data.children && data.children.length > 0;
  const indentStyle = level > 0 ? { paddingLeft: `${level * 24}px` } : {};

  console.log(`Rendering row for ${data.creator_name}:`, {
    hasChildren,
    childrenCount: data.children?.length || 0,
    level,
    isExpanded
  });

  const handleNameChange = (newName: string) => {
    if (!hasBuAccess) {
      applyOptimisticUpdate(data.id, 'creator_name', newName);
    }
  };

  return (
    <>
      {/* Main Row */}
      <tr className="border-b hover:bg-muted/50 transition-colors">
        {/* Expand/Collapse Button */}
        <td className="px-4 py-3 w-8">
          <div style={indentStyle}>
            {hasChildren ? (
              <button
                onClick={() => {
                  console.log(`Toggling expansion for ${data.creator_name}:`, !isExpanded);
                  setIsExpanded(!isExpanded);
                }}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
                aria-label={isExpanded ? "Collapse children" : "Expand children"}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : level > 0 ? (
              <div className="w-6 h-6 flex items-center justify-center">
                <div className="w-2 h-2 bg-muted-foreground/40 rounded-full"></div>
              </div>
            ) : null}
          </div>
        </td>

        {/* Creator Name - Using CreatorInfo */}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2" style={indentStyle}>
            <CreatorInfo 
              name={data.creator_name} 
              handler={data.social_media_handler} 
              platform={data.social_media_type} 
              region={data.region}
              profileImageUrl={data.profile_image_url}
              editable={!hasBuAccess}
              onNameChange={handleNameChange}
              isPending={isPending(data.id, 'creator_name')}
              isOptimisticProcessing={isOptimisticProcessing}
            />
            {level > 0 && (
              <Badge variant="outline" className="text-xs ml-2">
                Child
              </Badge>
            )}
          </div>
        </td>


        {/* KOL Type */}
        <td className="px-4 py-3">
          {hasBuAccess ? (
            <div className="inline-flex items-center px-2 py-1 rounded-md bg-purple-50 text-purple-700 text-xs font-medium border border-purple-200">
              {data.kol_type || 'Not set'}
            </div>
          ) : (
            <SearchableSelect
              options={[
                { label: 'Airdrop', value: 'Airdrop' },
                { label: 'Defi', value: 'Defi' },
                { label: 'General', value: 'General' },
                { label: 'GameFi', value: 'GameFi' },
                { label: 'News', value: 'News' }
              ]}
              value={data.kol_type || ''}
              onChange={(value) => handleUpdateKolType(data.id, value)}
              placeholder="Select type..."
              badgeVariant="purple"
              className="w-full"
              disabled={isPending(data.id, 'kol_type')}
            />
          )}
        </td>

        {/* Internal KOL Type */}
        <td className="px-4 py-3">
          {hasBuAccess ? (
            <div className="inline-flex items-center px-2 py-1 rounded-md bg-orange-50 text-orange-700 text-xs font-medium border border-orange-200">
              {data.internal_kol_type || 'Not set'}
            </div>
          ) : (
            <SearchableSelect
              options={INTERNAL_KOL_TYPE_OPTIONS}
              value={data.internal_kol_type || ''}
              onChange={(value) => handleUpdateInternalKolType(data.id, value)}
              placeholder="Select type..."
              badgeVariant="default"
              className="w-full"
              disabled={isPending(data.id, 'internal_kol_type')}
            />
          )}
        </td>

        {/* Region - Editable */}
        <td className="px-4 py-3">
          {hasBuAccess ? (
            <div className="inline-flex items-center px-2 py-1 rounded-md bg-purple-50 text-purple-700 text-xs font-medium border border-purple-200">
              {data.region || 'Not set'}
            </div>
          ) : (
            <SearchableSelect
              options={REGION_OPTIONS}
              value={data.region || ''}
              onChange={(value) => handleUpdateRegion(data.id, value)}
              placeholder="Select region..."
              badgeVariant="purple"
              className="w-full"
              disabled={isPending(data.id, 'region')}
            />
          )}
        </td>

        {/* Tier - Editable */}
        <td className="px-4 py-3">
          {hasBuAccess ? (
            <div className="inline-flex items-center px-2 py-1 rounded-md bg-gray-50 text-gray-700 text-xs font-medium border border-gray-200">
              {data.tier || 'Not set'}
            </div>
          ) : (
            <SearchableSelect
              options={TIER_OPTIONS}
              value={data.tier || ''}
              onChange={(value) => handleUpdateTier(data.id, value)}
              placeholder="Select tier..."
              badgeVariant="dark"
              className="w-full"
              disabled={isPending(data.id, 'tier')}
            />
          )}
        </td>

        {/* Content Type */}
        <td className="px-4 py-3">
          <MultiSelectContentTypes
            value={data.content_type || []}
            onChange={(newValues) => handleUpdateContentTypes(data.id, newValues)}
            options={contentTypeOptions.map(option => option.value)}
            disabled={hasBuAccess || isPending(data.id, 'content_type')}
          />
        </td>

        {/* PIC */}
        <td className="px-4 py-3">
          <MultiSelectPIC
            value={data.pic_id || []}
            onChange={(newValues) => handleUpdatePIC(data.id, newValues)}
            options={picOptions}
            disabled={hasBuAccess || isPending(data.id, 'pic_id')}
          />
        </td>
      </tr>

      {/* Child Rows */}
      {isExpanded && hasChildren && (
        <>
          {console.log(`Rendering ${data.children!.length} children for ${data.creator_name}`)}
          {data.children!.map((child) => (
            <KolHierarchicalDictionaryRow
              key={child.id}
              data={child}
              onUpdate={onUpdate}
              isPending={isPending}
              isOptimisticProcessing={isOptimisticProcessing}
              contentTypeOptions={contentTypeOptions}
              picOptions={picOptions}
              handleUpdateContentTypes={handleUpdateContentTypes}
              handleUpdatePIC={handleUpdatePIC}
              handleUpdateKolType={handleUpdateKolType}
              handleUpdateInternalKolType={handleUpdateInternalKolType}
              handleUpdateRegion={handleUpdateRegion}
              handleUpdateTier={handleUpdateTier}
              applyOptimisticUpdate={applyOptimisticUpdate}
              level={level + 1}
            />
          ))}
        </>
      )}
    </>
  );
}
